import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/base/Breadcrumb';
import { categories } from '@/mocks/categories';
import { getArticlesByCategory, getCategoryName } from '@/mocks/articles';
import { generateSEOMeta, generateSchemaOrg } from '@/components/base/SEOData';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();

  const category = categories.find((c) => c.slug === slug);
  const categoryName = slug ? getCategoryName(slug) : '';
  const categoryArticles = slug ? getArticlesByCategory(slug) : [];

  useEffect(() => {
    if (!slug) return;
    const title = `${categoryName} - 分类`;
    const desc = category?.description || `浏览${categoryName}相关的专业健康知识文章`;

    const seoMeta = generateSEOMeta({
      title,
      description: desc,
      keywords: `${categoryName},膀胱健康,泌尿健康,健康科普`,
      canonicalUrl: `/category/${slug}`,
      ogType: 'website',
      ogImage: 'https://readdy.ai/api/search-image?query=Warm%20abstract%20wellness%20health%20concept%20with%20soft%20cream%20and%20golden%20tones%2C%20minimalist%20medical%20aesthetic&width=1200&height=630&seq=og-category&orientation=landscape',
    });

    const existingMeta = document.querySelector('script[data-seo-meta]');
    if (existingMeta) existingMeta.remove();
    const metaScript = document.createElement('script');
    metaScript.setAttribute('data-seo-meta', 'true');
    metaScript.type = 'application/json';
    metaScript.textContent = seoMeta;
    document.head.appendChild(metaScript);

    document.title = `${categoryName} - 膀胱健康知识网`;
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', desc);
  }, [slug, categoryName, category?.description]);

  if (!category) {
    return (
      <>
        <Navbar />
        <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-20 text-center">
          <h1 className="font-heading text-2xl font-bold text-foreground-900 mb-4">分类未找到</h1>
          <Link to="/" className="text-primary-500 hover:text-primary-600 text-sm cursor-pointer">返回首页</Link>
        </div>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main>
        <div className="w-full bg-background-100 border-b border-background-200/70">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: categoryName },
              ]}
            />
            <div className="pb-8 md:pb-10">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-primary-50 text-primary-500">
                  <i className={`${category.icon} text-xl md:text-2xl`}></i>
                </div>
                <div>
                  <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">
                    {categoryName}
                  </h1>
                  <p className="text-sm text-foreground-600 mt-1">{category.description}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full bg-background-100 min-h-[400px]">
          <div className="w-full max-w-[1280px] mx-auto px-4 md:px-6 py-8 md:py-10">
            <div className="flex items-center gap-2 mb-6 md:mb-8 flex-wrap">
              <span className="text-sm text-foreground-600 whitespace-nowrap">分类筛选：</span>
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`inline-flex items-center px-4 py-2 rounded-full text-sm font-medium transition-colors duration-200 whitespace-nowrap cursor-pointer ${
                    slug === cat.slug
                      ? 'bg-primary-500 text-white'
                      : 'bg-background-50 text-foreground-700 border border-background-200/70 hover:border-primary-300 hover:text-primary-500'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>

            {categoryArticles.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 mb-4">
                  <i className="ri-file-list-3-line text-2xl"></i>
                </div>
                <p className="text-foreground-600 text-sm">该分类下暂无文章</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5">
                {categoryArticles.map((article) => (
                  <Link
                    key={article.slug}
                    to={`/article/${article.slug}`}
                    className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-primary-200 transition-all duration-300 cursor-pointer"
                  >
                    <div className="relative w-full h-44 md:h-48 overflow-hidden">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                      />
                    </div>
                    <div className="p-4">
                      <h2 className="font-heading text-sm md:text-base font-semibold text-foreground-900 mb-2 line-clamp-2 leading-snug">
                        {article.title}
                      </h2>
                      <p className="text-xs md:text-sm text-foreground-600 line-clamp-2 mb-3 leading-relaxed">
                        {article.excerpt}
                      </p>
                      <div className="flex items-center justify-between text-xs text-foreground-500">
                        <span className="whitespace-nowrap">{article.publishDate}</span>
                        <span className="whitespace-nowrap">{article.readTime}阅读</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}